import { z } from 'zod';

/*
 * Reusing content: a course, quiz or workshop can be
 * - used as is (assigned to members; courses),
 * - customized (an overlay that keeps receiving the source's updates), or
 * - copied (a fork: an independent copy that records its source).
 * Who may: everyone for Nirvik's library, and the owner org and the orgs
 * under it for an org's own content. The owner can switch each off.
 */

export const reusePolicySchema = z.object({
  use: z.boolean().default(true),
  customize: z.boolean().default(true),
  fork: z.boolean().default(true),
});
export type ReusePolicy = z.infer<typeof reusePolicySchema>;

/** Stored null means everything is allowed. */
export const reuseOf = (p: Partial<ReusePolicy> | null | undefined): ReusePolicy => ({
  use: p?.use ?? true,
  customize: p?.customize ?? true,
  fork: p?.fork ?? true,
});

export const REUSE_KINDS = ['course', 'quiz', 'event'] as const;
export type ReuseKind = (typeof REUSE_KINDS)[number];

export const reuseQuerySchema = z.object({
  kind: z.enum(REUSE_KINDS),
  q: z.string().trim().max(100).optional(),
});

/** Where the copy goes: an org, or Nirvik's library (null). */
export const forkInputSchema = z.object({
  ownerOrgId: z.string().uuid().nullable(),
});

/** A copy's source, for its editor ("Copied from … — a newer version is out"). */
export interface ContentSource {
  id: string;
  title: string;
  ownerName: string;
  /** The source's version when this was copied, and its latest now. */
  version: number | null;
  latestVersion: number | null;
  /** Its public page, when it has one (/c/…, /q/…, /e/…). */
  slug: string | null;
}

/** A customized quiz's changes to its source: the source questions it leaves out. */
export interface QuizOverlay {
  hidden: string[];
}

/** A customized quiz's editor view of its source (prompts only; answers stay on the server). */
export interface QuizOverlayView {
  sourceVersion: number | null;
  questions: { id: string; prompt: string; skill: string; type: string }[];
  hidden: string[];
  goneFromSource: number;
}

/** A customized course's changes to its source (see the API's services/course-overlays.ts). */
export interface CourseOverlay {
  /** Source lessons and modules it leaves out (so new source versions don't bring them back). */
  hiddenLessons: string[];
  hiddenModules: string[];
  /** Per source lesson: a different title, and a note shown at its top. */
  lessonTitles: Record<string, string>;
  lessonNotes: Record<string, string>;
}

export const emptyCourseOverlay = (): CourseOverlay => ({
  hiddenLessons: [],
  hiddenModules: [],
  lessonTitles: {},
  lessonNotes: {},
});

export const sourceLessonChangeSchema = z.object({
  /** Null clears the override. */
  title: z.string().trim().min(1).max(200).nullable().optional(),
  note: z.string().trim().max(5000).nullable().optional(),
});
